import { Elements } from '@stripe/react-stripe-js';

import React, { Component, Fragment, PureComponent } from 'react';

import styled from 'styled-components';

import { AppDispatch } from 'redux-types';
import { CurrenciesState } from '@redux/reducers/currencies/types';
import {
  ProfileWithFullName,
  PurchasedSubscription as PurchasedSubscriptionType,
} from '@redux/reducers/profile/types';
import { QueryState } from '@redux/reducers/queryParams/types';
import {
  acceptRenewalOffer,
  changeCard,
  toggleAutomaticRenewal,
} from '@redux/reducers/profile/action';
import { fullLogout } from '@redux/reducers/auth/action';
import { loadApplications } from '@redux/reducers/applications/action';

import Border from '../atoms/Border/Border';
import Box from '../atoms/Box/Box';
import Button from '../atoms/Button/Button';
import CSSMediaQuery, { ShowOn } from '../atoms/CSSMediaQuery/CSSMediaQuery';
import Circle from '../atoms/Circle/Circle';
import { Col, Row } from '../atoms/Grid';
import Copyright from '@components/atoms/Copyright/Copyright';
import ExternalLink from '../atoms/ExternalLink/ExternalLink';
import Link from '../atoms/Link/Link';
import Text from '../atoms/Text/Text';
import AccountTitle from '../molecules/AccountTitle';
import { Accordion } from '../organisms/Accordion';
import AccountAccordionValue from '../organisms/AccountAccordionValue';
import {
  AccountControl,
  AccountControlGroup,
} from '../organisms/AccountControl';
import ManageSubscription from '../organisms/ManageSubscription';
import PurchasedPlatforms from '../organisms/PurchasedPlatforms';
import PurchasedSubscription from '../organisms/PurchasedSubscription/PurchasedSubscription';
import SignOutPopup from '@components/organisms/SignOutPopup';

import AnalyticsManager from '@utils/analyticsManager';
import { ROUTE } from '@utils/index';
import { isCN } from '@utils/origin';
import isServer from '@utils/isServer';

import { Align } from '@shared/enums/align';
import { B2C_EDU, SUBSCRIPTION_STATUS } from '@shared/subscription/constants';
import { DEFAULT_AVATAR, STORE_SOURCE } from '@shared/markup/constants';
import ExternalLinkIcon from '@public/icons/externalLink3.svg';
import { GroupedPlatforms } from '@shared/interfaces/grouped-platforms';
import { I18nTranslation } from '@shared/interfaces/i18n';
import { PROFILE_KINDS } from '@shared/profile/constants';
import { PricingCard } from '@shared/interfaces/pricing-card';
import { Session } from 'next-auth';
import { Target } from '@shared/enums/target';
import { breakpoint } from 'theme';
import {
  calculatePriceByPercent,
  getPriceISOString,
  getPriceYearString,
} from '@shared/price/functions';
import { env } from 'next-runtime-env';
import { i18n, withTranslation } from 'i18n';
import { loadStripe } from '@stripe/stripe-js';
import { withUser } from '@hoc/withUser';

type AccountInfoProps = {
  onChangeAvatar?: () => void;
  onChangeInfo?: () => void;
  onChangePassword?: () => void;
  onChangeEmailPreferences?: () => void;
  kind?: number;
  verifiedEmail?: boolean;
  t: I18nTranslation;
  user?: Session['user'];
  updateSession: () => void;
};

class AccountInfo extends PureComponent<AccountInfoProps> {
  get avatar() {
    const { user } = this.props;
    const image = user?.image;

    return (
      <BoxAvatar>
        <CircleImage
          backgroundColor="white"
          size={7}
          userImage={image || DEFAULT_AVATAR}
        />
      </BoxAvatar>
    );
  }

  get userKind() {
    const { kind } = this.props;

    if (!kind) return null;

    const { key } =
      PROFILE_KINDS.find(kind => kind.value === this.props.kind) || {};

    if (!key) return null;

    const translatedName = this.props.t(`profiles.${key}`);

    return (
      <Box mt={0.5}>
        <TextUserData>{translatedName}</TextUserData>
      </Box>
    );
  }

  handleVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.props.updateSession();
    }
  };

  componentDidMount() {
    window.addEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
      false
    );
  }

  componentWillUnmount() {
    window.removeEventListener(
      'visibilitychange',
      this.handleVisibilityChange,
      false
    );
  }

  render() {
    const { onChangeInfo, t, user } = this.props;

    const userName = user?.name;
    const email = user?.email;

    return (
      <Box smMb={2.5} mb={4}>
        <BoxBorder
          smMb={0}
          mb={2.125}
          smPl={1.5}
          smPr={1.5}
          pl={2.375}
          pr={2.375}
        >
          <Row middle="xs" gutterM={1.75}>
            <Col xs={12} sm={4}>
              {this.avatar}
            </Col>

            <Col xs={12} sm={8}>
              <Box smAlign={Align.Center} smMt={1.125} smMb={1.625}>
                {userName && (
                  <Box>
                    <Text
                      smSize={2}
                      size={2.25}
                      lineHeight="normal"
                      color="cornflowerBlue"
                    >
                      {userName}
                    </Text>
                  </Box>
                )}

                {this.userKind}

                <Box mt={0.5}>
                  <TextUserData>{email}</TextUserData>
                </Box>

                <ManageBox mt={0.5}>
                  <StyledExternalLinkIcon />
                  <Button onClick={onChangeInfo}>
                    <ManageAccountLink color="abyss">
                      {t('manageAccount')}
                    </ManageAccountLink>
                  </Button>
                </ManageBox>
              </Box>
            </Col>
          </Row>
        </BoxBorder>
      </Box>
    );
  }
}

const AccountInfoWithUser = withUser(AccountInfo);

type RequestLinksProps = {
  onChangeInfo?: () => void;
  onLogout?: () => void;
  t: I18nTranslation;
  dispatch: AppDispatch;
};

class RequestLinks extends PureComponent<RequestLinksProps> {
  state = {
    showSignOutPopup: false,
  };

  handleOpenSignOutPopup = () => {
    this.setState({
      showSignOutPopup: true,
    });
  };

  handleFullLogout = () => {
    this.props.dispatch(fullLogout());
  };

  handleCloseSignOutPopup = () => {
    this.setState({
      showSignOutPopup: false,
    });
  };

  render() {
    const { t } = this.props;

    const informationLink = t('requests.infoLink');

    return (
      <Fragment>
        <AccountTitle>{t('requests.privacy')}</AccountTitle>
        <PrivacySecurityGroup>
          <ExternalLink href={informationLink} target={Target.Blank}>
            <BoldControlText iconNameLeft="trace">
              {t('requests.info')}
            </BoldControlText>
          </ExternalLink>
          <Link href={ROUTE.ACCOUNT.PREFERENCES.INDEX} target={Target.Blank}>
            <BoldControlText iconNameLeft="trash">
              {t('requests.delete')}
            </BoldControlText>
          </Link>
        </PrivacySecurityGroup>

        <SignOutGroup>
          <BoldControlText
            onClick={this.handleFullLogout}
            color="monza"
            iconNameRight="onOff"
            iconColor="monza"
          >
            {t('info.logout')}
          </BoldControlText>
        </SignOutGroup>

        {this.state.showSignOutPopup && (
          <SignOutPopup
            dispatch={this.props.dispatch}
            handleCloseSignOutPopup={this.handleCloseSignOutPopup}
          />
        )}
      </Fragment>
    );
  }
}

const StyledCopyright = styled(Copyright)`
  margin: 0 0;
`;

const Wrapper = styled(Box)`
  flex-grow: 1;
`;

const CircleImage = styled(Circle)<{ userImage?: string }>`
  position: relative;
  background-position: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-image: url(${props => props.userImage});

  ${breakpoint.m`
    width: 11.25em;
    height: 11.25em;
  `}
`;

const PrivacySecurityGroup = styled(AccountControlGroup)`
  border-bottom: 1px solid ${({ theme }) => theme.transparentColors.nevada20};
`;

const SignOutGroup = styled(AccountControlGroup)`
  margin-top: 4em;
`;

const BoldControlText = styled(AccountControl)`
  .text {
    font-weight: 700;
  }
`;

const ManageBox = styled(Box)`
  display: block;
  align-items: center;

  ${breakpoint.m`
    display: flex;
  `};
`;

const StyledExternalLinkIcon = styled(ExternalLinkIcon)`
  width: 25px;
  height: 25px;
  margin-right: 0.2em;
  padding-top: 0.2em;

  ${breakpoint.m`
    padding-top: 0em;
  `};
`;

const ManageAccountLink = styled(Text)`
  color: ${({ theme }) => theme.colors.abyss};
  font-size: 1.125em;
  line-height: normal;
  text-transform: uppercase;
  text-decoration: underline;
`;

const BoxContainer = styled(Box)`
  width: 100%;
  margin: 2em auto;
  max-width: 43em;

  ${breakpoint.m`
    margin-top: 3.75em;
    margin-bottom: 3.75em;
  `}
`;

const BoxControl = styled(Box)`
  border-top: 1px solid ${({ theme }) => theme.colors.iron};
  padding: 0.75em 1em;
  display: flex;
  flex-direction: column;
  justify-content: center;

  ${breakpoint.m`
    padding-top: 1.125em;
    padding-bottom: 1.125em;
    padding-left: 0;
    padding-right: 1.875em;
  `}
  &:last-child {
    border-bottom: 1px solid ${({ theme }) => theme.colors.iron};
  }
`;

const BoxAvatar = styled(Box)`
  ${breakpoint.down('ltm')`
    max-width: 7em;
    margin-left: auto;
    margin-right: auto;
  `}
`;

const BoxBorder = styled(Box)`
  ${breakpoint.m`
    padding-top: 1.5em;
    padding-bottom: 1.5em;
    border-bottom: 1px solid ${({ theme }) => theme.transparentColors.nevada20};
  `}
`;

const TextUserData = styled(Text)`
  font-size: 1.125em;
  line-height: normal;
  color: ${props => props.color || props.theme.colors.cornflowerBlue};
  ${breakpoint.m`
    font-size: 1.5em;
  `};
`;

const queryParams = {
  subscription: 'subscription',
};

const stripePromise = !isCN
  ? loadStripe(env('NEXT_PUBLIC_STRIPE_PUBLIC_KEY'))
  : null;

type AccountTemplateProps = {
  profile: ProfileWithFullName;
  onLogout?: () => void;
  onChangeAvatar?: () => void;
  onChangeInfo?: () => void;
  onChangePassword?: () => void;
  onChangeEmailPreferences?: () => void;
  onReactive?: () => void;
  onComparePlans?: () => void;
  query: QueryState;
  dispatch: AppDispatch;
  subscriptions: PurchasedSubscriptionType[];
  groupedPlatforms: GroupedPlatforms;
  formattedApplications: Record<string, string>;
  prices: PricingCard[];
  currencies: CurrenciesState;
  t: I18nTranslation;
};

class AccountTemplate extends Component<AccountTemplateProps> {
  private editCardTimeout;

  state = {
    loading: false,
    statusEditCard: {},
    showEditCard: false,
    showAutoRenewWarning: false,
    showBillingRetryWarning: false,
    showSubscriptionError: false,
    isGettingOffer: false,
  };

  actionWithSubscription = async action => {
    this.setState({
      loading: true,
      showSubscriptionError: false,
    });

    try {
      await this.props.dispatch(action);
      this.props.dispatch(loadApplications());
    } catch (error) {
      this.setState({
        showSubscriptionError: true,
      });
    }

    this.setState({
      loading: false,
      showAutoRenewWarning: false,
      showBillingRetryWarning: false,
    });
  };

  toggleOffAutoRenewSwitch = bundle => {
    AnalyticsManager.shared.accountPage.turnOffAutoRenewWarning();
    AnalyticsManager.googleLegacy.accountPage.turnOffAutoRenewWarning();
    this.actionWithSubscription(toggleAutomaticRenewal(bundle));
  };

  changeCard = async tokens => {
    const { t } = this.props;

    try {
      this.props.dispatch(changeCard(tokens));
      this.setState({
        loading: false,
        statusEditCard: {
          type: 'success',
          message: t('card.message'),
        },
      });

      this.editCardTimeout = setTimeout(() => {
        this.setState({
          showEditCard: false,
          statusEditCard: {},
        });
      }, 2000);
    } catch (error) {
      this.setState({
        loading: false,
        statusEditCard: {
          type: 'error',
          message: t('card.error'),
        },
      });
    }
  };

  getTokensPromises = (currencies = [], stripe, cardElement) => {
    return currencies.reduce(acc => {
      return [...acc, stripe.createToken(cardElement)];
    }, []);
  };

  getTokens = (responses = [], currencies) => {
    return responses.reduce((prev, current, i) => {
      return {
        ...prev,
        [currencies[i]]: current.token.id,
      };
    }, {});
  };

  handleClickSwitchAutoRenew = (
    bundle,
    hasActive,
    hasBillingRetry,
    isApplicableOffer
  ) => {
    if (hasBillingRetry) {
      this.setState({
        showBillingRetryWarning: bundle,
      });

      return;
    }

    if (hasActive) {
      AnalyticsManager.shared.accountPage.turnOffAutoRenewSwitch();
      AnalyticsManager.googleLegacy.accountPage.turnOffAutoRenewSwitch();
      // For case when a subscription was purchased via PayPal
      if (!isApplicableOffer) {
        this.actionWithSubscription(toggleAutomaticRenewal(bundle));
      } else {
        this.setState({
          showAutoRenewWarning: bundle,
        });
      }

      return;
    }

    AnalyticsManager.shared.accountPage.turnOnAutoRenewSwitch();
    AnalyticsManager.googleLegacy.accountPage.turnOnAutoRenewSwitch();
    this.actionWithSubscription(toggleAutomaticRenewal(bundle));
  };

  handleSubmit = async (stripe, cardElement) => {
    const {
      profile: { currencies },
      t,
    } = this.props;

    this.setState({
      loading: true,
      statusEditCard: {},
    });

    const promises = this.getTokensPromises(currencies, stripe, cardElement);
    const responses = await Promise.all(promises);
    const error = responses.find(
      (response: { error: string }) => response.error
    ) as { message: string };
    if (error) {
      this.setState({
        loading: false,
        statusEditCard: {
          type: 'error',
          message: error.message || t('card.unknownError'),
        },
      });
    } else {
      const tokens = this.getTokens(responses, currencies);
      this.changeCard(tokens);
    }
  };

  handleGetOffer = bundle => {
    AnalyticsManager.shared.accountPage.getOfferWarning();
    AnalyticsManager.googleLegacy.accountPage.getOfferWarning();

    this.setState({
      isGettingOffer: true,
    });

    this.props
      .dispatch(acceptRenewalOffer(bundle))
      .then(() => {
        this.setState({
          isGettingOffer: false,
        });
        this.props.dispatch(loadApplications());
      })
      .catch(() => {
        this.setState({
          isGettingOffer: false,
        });
      });
  };

  handleCloseAutoRenewWarning = () => {
    this.setState({
      showAutoRenewWarning: false,
    });
  };

  handleShowEditCardPopup = bundle => {
    this.setState({
      showEditCard: bundle,
    });
  };

  handleCloseEditCardPopup = () => {
    this.setState({
      showEditCard: false,
      statusEditCard: {},
    });
  };

  handleCloseBillingRetryWarning = () => {
    this.setState({
      showBillingRetryWarning: false,
    });
  };

  componentWillUnmount() {
    clearTimeout(this.editCardTimeout);
  }

  renderOurStoreControl = (
    bundle,
    hasActive,
    hasBillingRetry,
    offerCurrentPrice,
    offerPrice,
    isAcceptedOffer,
    isApplicableOffer,
    expireDate
  ) => {
    const currentLanguage = i18n.language;

    let origin = '';

    if (!isServer()) {
      origin = window?.location?.origin;
    }

    return (
      <Elements
        stripe={stripePromise}
        options={{
          locale: currentLanguage,
          fonts: [
            {
              family: 'Main',
              src: `url(${origin}${
                require('../../public/fonts/en/SourceSansProRegular.ttf')
                  .default
              })`,
              weight: '400',
            },
            {
              family: 'Main',
              src: `url(${origin}${
                require('../../public/fonts/zh/subsets/NotoSansSCRegular-subset.woff2')
                  .default
              })`,
              weight: '400',
              unicodeRange: 'U+4E00-9FFF',
            },
            {
              family: 'Main',
              src: `url(${origin}${
                require('../../public/fonts/zh/subsets/NotoSansSCRegular-subset.woff2')
                  .default
              })`,
              weight: '400',
              unicodeRange: 'U+2B740–2B81F',
            },
          ],
        }}
      >
        <ManageSubscription
          bundle={bundle}
          last4={this.props.profile.last4}
          month={this.props.profile.month}
          year={this.props.profile.year}
          cardBrand={this.props.profile.brand}
          hasActive={hasActive}
          hasBillingRetry={hasBillingRetry}
          toggleOffAutoRenewSwitch={this.toggleOffAutoRenewSwitch}
          onClickSwitchAutoRenew={this.handleClickSwitchAutoRenew}
          loading={this.state.loading}
          statusEditCard={this.state.statusEditCard}
          showEditCard={this.state.showEditCard}
          showAutoRenewWarning={this.state.showAutoRenewWarning}
          showBillingRetryWarning={this.state.showBillingRetryWarning}
          showSubscriptionError={this.state.showSubscriptionError}
          onSubmit={this.handleSubmit}
          onClickGetOffer={this.handleGetOffer}
          offerDiscount={this.props.profile.offerDiscount}
          offerCurrentPrice={offerCurrentPrice}
          offerPrice={offerPrice}
          isAcceptedOffer={isAcceptedOffer}
          isApplicableOffer={isApplicableOffer}
          isGettingOffer={this.state.isGettingOffer}
          onCloseAutoRenewWarning={this.handleCloseAutoRenewWarning}
          onShowEditCardPopup={this.handleShowEditCardPopup}
          onCloseEditCardPopup={this.handleCloseEditCardPopup}
          onCloseBillingRetryWarning={this.handleCloseBillingRetryWarning}
          expireDate={expireDate}
        />
      </Elements>
    );
  };

  renderOfficialStoreControl = storeLink => {
    const { t } = this.props;

    const renewT = key => t(`storeControl.renew.${key}`);
    const billingT = key => t(`storeControl.billing.${key}`);

    return (
      <Fragment>
        <BoxControl>
          <Accordion>
            {({ on, onChange, duration }) => {
              return (
                <AccountAccordionValue
                  title={renewT('title')}
                  onChange={onChange}
                  on={on}
                  duration={duration}
                >
                  {renewT('text')}
                  <ExternalLink href={storeLink} target={Target.Blank}>
                    {t('storeControl.appStore')}
                  </ExternalLink>
                </AccountAccordionValue>
              );
            }}
          </Accordion>
        </BoxControl>
        <BoxControl>
          <Accordion>
            {({ on, onChange, duration }) => {
              return (
                <AccountAccordionValue
                  title={billingT('title')}
                  onChange={onChange}
                  on={on}
                  duration={duration}
                >
                  {billingT('text')}
                  <ExternalLink href={storeLink} target={Target.Blank}>
                    {t('storeControl.appStore')}
                  </ExternalLink>
                </AccountAccordionValue>
              );
            }}
          </Accordion>
        </BoxControl>
      </Fragment>
    );
  };

  renderGreeting = () => {
    const { t } = this.props;

    return (
      <CSSMediaQuery showOn={[ShowOn.Tablet, ShowOn.Desktop]}>
        <Box mb={1.625}>
          <Box mb={0.5}>
            <Text
              lineHeight="normal"
              weight="bold"
              size={3.75}
              color="cornflowerBlue"
            >
              {t('title')}
            </Text>
          </Box>
          <Text color="cornflowerBlue" size={1.5} lineHeight="1.5">
            {t('greeting.text')}
          </Text>
        </Box>
      </CSSMediaQuery>
    );
  };

  renderNoActiveSub = () => {
    const { t } = this.props;

    return (
      <Box smMl={1} smMr={1}>
        <Box mb={1.75}>
          <Text
            smSize={1.125}
            size={1.5}
            lineHeight="normal"
            color="cornflowerBlue"
          >
            {t('subs.noActive')}
          </Text>
        </Box>
        <Box mb={0.5}>
          <Text
            smSize={1.125}
            size={1.5}
            weight={600}
            lineHeight="normal"
            color="cornflowerBlue"
          >
            {t('subs.expiredLicenses')}
          </Text>
        </Box>
      </Box>
    );
  };

  renderSubs = (subscriptions, currencies) => {
    const t = (key, options?) => this.props.t(`subs.${key}`, options);

    const hasActiveSub = subscriptions.some(item => item.hasExpired === false);

    const filteredSubscriptions = hasActiveSub
      ? subscriptions.filter(sub => sub.hasExpired === false)
      : subscriptions;

    return (
      <Box smMb={2.5} mb={4}>
        <AccountTitle>{t('title')}</AccountTitle>

        {!hasActiveSub && this.renderNoActiveSub()}

        {filteredSubscriptions.map((item, i) => {
          const originalSubscription = item.originalSubscription;
          const hasOriginalSubscription = !!item.originalSubscription;
          const subscription = item.subscription;
          const isAlipay = subscription.payment_system === 'alipay';
          const hasExpired = item.hasExpired;
          const billingHistory = item.billingHistory;
          const fromOurStore = subscription.store === STORE_SOURCE.OUR;
          const renewalAmount = subscription.renewalAmount;
          const renewalCurrency = subscription.renewalCurrency;
          const fromOfficialStore =
            subscription.store === STORE_SOURCE.OFFICIAL;

          let price = null;
          let offerCurrentPrice = null;
          let offerPrice = null;

          if (hasOriginalSubscription && !isAlipay) {
            const { selected } = this.props.prices.find(
              price => price.bundle === originalSubscription.bundle
            );

            const currencySymbol = currencies.find(
              currency => currency.iso === renewalCurrency
            )?.symbol ?? selected.symbol;

            if (typeof window !== 'undefined' && window.localStorage) {
              localStorage.setItem('currencySymbol', currencySymbol);
            }
            
            price = getPriceYearString(
              currencySymbol,
              renewalAmount ?? (selected.price || selected.regPrice),
              renewalCurrency ?? selected.iso
            );
            offerCurrentPrice = getPriceISOString(
              selected.symbol,
              selected.price ?? selected.regPrice,
              selected.iso
            );

            const calculatedOfferPrice = calculatePriceByPercent(
              selected.price ?? selected.regPrice,
              this.props.profile.offerDiscount
            );
            offerPrice = getPriceISOString(
              selected.symbol,
              calculatedOfferPrice,
              selected.iso
            );
          }

          const hasBillingRetry =
            subscription.status === SUBSCRIPTION_STATUS.BILLING_RETRY;
          const hasActive = subscription.status === SUBSCRIPTION_STATUS.ACTIVE;

          const hideForB2C = subscription.bundle
            .toLowerCase()
            .includes(B2C_EDU);

          return (
            <Box mb={4} key={i}>
              <Border>
                <PurchasedSubscription
                  appName={item.appName}
                  purchasedDate={subscription.createdTimestamp}
                  expireDate={subscription.expire}
                  licenseName={originalSubscription ? originalSubscription.name : ''}
                  licenseImage={subscription.image}
                  fromOurStore={fromOurStore}
                  fromOfficialStore={fromOfficialStore}
                  storeName={subscription.storeName}
                  onReactive={this.props.onReactive.bind(
                    this,
                    originalSubscription
                  )}
                  onComparePlans={this.props.onComparePlans}
                  hasExpired={hasExpired}
                  hasActive={hasActive}
                  hasBillingRetry={hasBillingRetry}
                  billingHistory={billingHistory}
                  price={price}
                  hasOriginalSubscription={hasOriginalSubscription}
                  hasAlipayRestrictions={isAlipay}
                  hideForB2C={hideForB2C}
                />
              </Border>

              {!isAlipay &&
                fromOurStore &&
                (!hasExpired || hasBillingRetry) && (
                  <Border>
                    {this.renderOurStoreControl(
                      subscription.bundle,
                      hasActive,
                      hasBillingRetry,
                      offerCurrentPrice,
                      offerPrice,
                      subscription.offerAccepted,
                      subscription.offerApplicable,
                      subscription.expire
                    )}
                  </Border>
                )}

              {fromOfficialStore &&
                !hasExpired &&
                this.renderOfficialStoreControl(subscription.storeLink)}
            </Box>
          );
        })}
      </Box>
    );
  };

  render() {
    const {
      profile,
      query,
      onChangeAvatar,
      onChangeInfo,
      onLogout,
      onChangeEmailPreferences,
      onChangePassword,
      subscriptions,
      groupedPlatforms,
      formattedApplications,
      currencies,
      t,
    } = this.props;
    const isHiddenUI = query.state === queryParams.subscription;

    return (
      <Wrapper backgroundColor="white">
        <BoxContainer>
          {!isHiddenUI && this.renderGreeting()}

          {!isHiddenUI && (
            <AccountInfoWithUser
              onChangeAvatar={onChangeAvatar}
              onChangeInfo={onChangeInfo}
              onChangePassword={onChangePassword}
              onChangeEmailPreferences={onChangeEmailPreferences}
              kind={profile.kind}
              image={profile.image}
              verifiedEmail={profile.verifiedEmail}
              t={t}
            />
          )}

          {subscriptions.length > 0 && this.renderSubs(subscriptions, currencies)}
          {!subscriptions.length &&
            Object.keys(groupedPlatforms).length !== 0 && (
              <PurchasedPlatforms
                groupedPlatforms={groupedPlatforms}
                formattedApplications={formattedApplications}
              />
            )}

          {!isHiddenUI && (
            <RequestLinks
              t={t}
              onChangeInfo={onChangeInfo}
              onLogout={onLogout}
              dispatch={this.props.dispatch}
            />
          )}
        </BoxContainer>
        <StyledCopyright />
      </Wrapper>
    );
  }
}

export default withTranslation('account')(AccountTemplate);
